/* eslint-disable @typescript-eslint/ban-types */
import { combineReducers, createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import { composeWithDevTools, EnhancerOptions } from 'redux-devtools-extension';
import { persistStore, persistReducer, PersistConfig } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import sessionStorage from 'redux-persist/lib/storage/session';
import { SCREENSHOT_SUCCESS } from './checkout';
import { deviceReducer } from './deviceInfo/reducer';
import { applicationReducer } from './application/reducer';
import { sofaConfigurationReducer } from './sofaConfiguration/reducer';
import { localizationReducer } from './localization/reducer';
import { popOverReducer } from './popOver/reducer';
import { coverReducer } from './cover/reducer';
import { settingsReducer } from './settings/reducer';
import { modelsReducer } from './models/reducer';
import { productsReducer } from './products/reducer';
import { checkoutReducer } from './checkout/reducer';
import { screensaverReducer } from './screensaver/reducer';
import { swiperReducer } from './swiper/reducer';
import { actionButtonsReducer } from './actionButtons/reducer';
import { sceneReducer } from './scene/reducer';
import { missingReducer } from './missing/reducer';
import { openDesignReducer } from './openDesign/reducer';
import { armrestReducer } from './armrest/reducer';
import {
	iframeHandlerMiddleware,
	screensaverMiddleware,
	configurationMiddleware,
	navisionMiddleware,
	statisticsMiddleware,
	rotationIconMiddleware,
	coverMiddleware,
} from './middleware';
import { RootState, Actions } from '.';
import { ApplicationState } from './application/types';
import { SofaConfigurationState } from './sofaConfiguration/types';
import { PopOverState } from './popOver/types';
import { SettingsState } from './settings/types';
import { CheckoutState } from './checkout/types';
import { CoverState } from './cover/types';

/*
 * One configuration is needed for every reducer (and storage type, i.e. local/session).
 *The whitelist/blacklist only goes one level deep.
 */
const applicationSessionStorageConfig: PersistConfig<ApplicationState> = {
	key: 'application',
	storage: sessionStorage,
	// ONLY the items in the whitelist will be persisted
	whitelist: [
		'appView',
		'currentLightbox',
		'languageChanged',
		'coverChanged',
		'fakeData',
		'showIpexGalleryButton',
	],
};

const sofaSessionStorageConfig: PersistConfig<SofaConfigurationState> = {
	key: 'sofa',
	storage: sessionStorage,
	// ONLY the items in the whitelist will be persisted
	whitelist: ['predefinedConfigurationId', 'persistedConfiguration'],
};

const popOverLocalStorageConfig: PersistConfig<PopOverState> = {
	key: 'popover',
	storage,
	// ONLY the items in the whitelist will be persisted
	whitelist: ['bannerHasBeenShown', 'placementInformationHasBeenShown', 'buildTipHasBeenShown'],
};

const coverSessionStorageConfig: PersistConfig<CoverState> = {
	key: 'cover',
	storage: sessionStorage,
	// ONLY the items in the whitelist will be persisted
	whitelist: ['selectedCover', 'coverOnly', 'coverOnlySwitch', 'additionalCoverItems'],
};

const settingsSessionStorageConfig: PersistConfig<SettingsState> = {
	key: 'settings',
	storage: sessionStorage,
	// ONLY the items in the whitelist will be persisted
	whitelist: ['sofaRange'],
};
const checkoutSessionStorageConfig: PersistConfig<CheckoutState> = {
	key: 'checkout',
	storage: sessionStorage,
	// ONLY the items in the whitelist will be persisted
	whitelist: ['screenshot', 'vpcCode', 'noSummaryPageBackButton'],
};

const rootReducer = combineReducers({
	// Insert more reducers here
	deviceInfo: deviceReducer,
	application: persistReducer(applicationSessionStorageConfig, applicationReducer),
	sofaConfiguration: persistReducer(sofaSessionStorageConfig, sofaConfigurationReducer),
	localization: localizationReducer,
	cover: persistReducer(coverSessionStorageConfig, coverReducer),
	popOver: persistReducer(popOverLocalStorageConfig, popOverReducer),
	settings: persistReducer(settingsSessionStorageConfig, settingsReducer),
	models: modelsReducer,
	products: productsReducer,
	checkout: persistReducer(checkoutSessionStorageConfig, checkoutReducer),
	screensaver: screensaverReducer,
	swiper: swiperReducer,
	actionButtons: actionButtonsReducer,
	scene: sceneReducer,
	missing: missingReducer,
	openDesign: openDesignReducer,
	armrest: armrestReducer,
});

const middlewares = [
	thunk,
	navisionMiddleware,
	iframeHandlerMiddleware,
	screensaverMiddleware,
	configurationMiddleware,
	statisticsMiddleware,
	rotationIconMiddleware,
	coverMiddleware,
];

/*
 * Filter out big chunks of data from the redux devtools,
 * to improve debug performance and stability.
 */
const logMessage = '<< TOO MUCH DATA TO LOG >>';
const devtoolsOptions: EnhancerOptions = {
	actionSanitizer: (action) => {
		if (action.type === SCREENSHOT_SUCCESS) {
			return { ...action, data: logMessage };
		}
		return action;
	},
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	stateSanitizer: (state: any) => {
		return {
			...state,
			sofaConfiguration: {
				...state.sofaConfiguration,
				screenshot: logMessage,
			},
		};
	},
};
const composeEnhancers = composeWithDevTools(devtoolsOptions);

export const store = createStore<RootState, Actions, {}, {}>(
	rootReducer,
	composeEnhancers(
		// Insert middleware here
		applyMiddleware(...middlewares)
	)
);

export const persistor = persistStore(store);

export default {
	store,
	persistor,
};
